import { updateCampaigns } from 'actions/campains'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import { INVITE_EMAIL_DESIGN_SCOPE_ROWS } from 'features/InviteEmailDesign/inviteEmailDesignScopeRows'
import {
  BULK_MEDIUM_SCOPES,
  buildBulkMediumChangeNotice,
  getBulkMediumAffectedCampaignNames,
  getBulkMediumDropdownPlaceholder,
  getBulkMediumDropdownValue,
  getBulkMediumScopeState,
  getBulkMediumScopeStatus,
  getBulkMediumUpdateCategories
} from 'features/ManageCampaigns/utils/inviteBulkMediumHelpers'
import {
  inviteMediumDropdownOptions,
  inviteMediumLegendItems,
  inviteMediumNames
} from 'features/ManageCampaigns/utils/inviteMediumOptions'
import {
  CAMPAIGN_GROUP_MODE_OPTIONS,
  CAMPAIGN_GROUP_MODES
} from 'features/ManageCampaigns/utils/inviteCampaignGroupModes'
import { useEmailSubscriptionGate } from 'components/ReusableComponents/UnlockServiceDialog'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import campaignApi from 'services/api/campaign'

import styles from './InviteMessageScopeSettings.module.scss'

const MEDIUM_SCOPE_CONFIG = {
  [BULK_MEDIUM_SCOPES.all]: {
    scopeLabel: 'All campaigns',
    updateFn: campaignApi.updateAllMedium
  },
  [BULK_MEDIUM_SCOPES.ota]: {
    scopeLabel: 'OTA campaigns',
    updateFn: campaignApi.updateOtaMedium
  },
  [BULK_MEDIUM_SCOPES.direct]: {
    scopeLabel: 'Direct campaigns',
    updateFn: campaignApi.updateNonOtaMedium
  }
}

const CUSTOM_SCOPE_ROWS = INVITE_EMAIL_DESIGN_SCOPE_ROWS.filter(
  row => row.scope !== BULK_MEDIUM_SCOPES.all
)

const InviteDeliveryConfigurationSettings = ({
  campaigns = [],
  contentSubsidiaryId,
  campaignGroupMode,
  onCampaignGroupModeChange,
  mediumOverrides,
  onMediumOverridesChange
}) => {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const { promptEmailSubscriptionUpgrade } = useEmailSubscriptionGate()

  const [isUpdatingMedium, setIsUpdatingMedium] = useState(false)
  const [pendingMediumConfirm, setPendingMediumConfirm] = useState(null)

  const scopeStatus = useMemo(() => getBulkMediumScopeStatus(campaigns), [campaigns])
  const isCustomMode = campaignGroupMode === CAMPAIGN_GROUP_MODES.custom

  useEffect(() => {
    onMediumOverridesChange(previousOverrides => {
      let hasChanges = false
      const nextOverrides = { ...previousOverrides }

      Object.values(BULK_MEDIUM_SCOPES).forEach(scope => {
        if (!nextOverrides[scope]) {
          return
        }

        if (getBulkMediumScopeState(campaigns, scope).medium === nextOverrides[scope]) {
          delete nextOverrides[scope]
          hasChanges = true
        }
      })

      return hasChanges ? nextOverrides : previousOverrides
    })
  }, [campaigns, onMediumOverridesChange])

  function guardEmailSubscriptionAction(action) {
    if (promptEmailSubscriptionUpgrade()) {
      return
    }

    action()
  }

  function handleCampaignGroupModeChange(nextMode) {
    guardEmailSubscriptionAction(() => onCampaignGroupModeChange(nextMode))
  }

  function openMediumConfirmDialog(scope, medium) {
    const { scopeLabel, updateFn } = MEDIUM_SCOPE_CONFIG[scope]
    const campaignNames = getBulkMediumAffectedCampaignNames(campaigns, scope)

    if (!campaignNames.length) {
      addToast(`No campaigns found for ${scopeLabel.toLowerCase()}`, { appearance: 'warning' })
      return
    }

    setPendingMediumConfirm({
      scope,
      scopeLabel,
      medium,
      mediumLabel: inviteMediumNames[medium] || medium,
      updateFn,
      campaignNames: [...campaignNames].sort((a, b) => a.localeCompare(b)),
      notice: buildBulkMediumChangeNotice({
        scope,
        medium,
        campaigns,
        mediumNames: inviteMediumNames
      })
    })

    onMediumOverridesChange(previousOverrides => {
      const nextOverrides = { ...previousOverrides, [scope]: medium }

      if (scope === BULK_MEDIUM_SCOPES.all) {
        delete nextOverrides[BULK_MEDIUM_SCOPES.ota]
        delete nextOverrides[BULK_MEDIUM_SCOPES.direct]
      }

      return nextOverrides
    })
  }

  function revertPendingMediumOverride(pendingConfirm) {
    if (!pendingConfirm) {
      return
    }

    onMediumOverridesChange(previousOverrides => {
      const nextOverrides = { ...previousOverrides }

      if (nextOverrides[pendingConfirm.scope] === pendingConfirm.medium) {
        delete nextOverrides[pendingConfirm.scope]
      }

      return nextOverrides
    })
  }

  async function applyMediumUpdate() {
    if (!pendingMediumConfirm) {
      return
    }

    const { updateFn, medium, scopeLabel, scope } = pendingMediumConfirm
    setIsUpdatingMedium(true)

    try {
      await updateFn({
        medium,
        categories: getBulkMediumUpdateCategories(scope)
      })
      onMediumOverridesChange(previousOverrides => {
        const nextOverrides = { ...previousOverrides, [scope]: medium }

        if (scope === BULK_MEDIUM_SCOPES.all) {
          delete nextOverrides[BULK_MEDIUM_SCOPES.ota]
          delete nextOverrides[BULK_MEDIUM_SCOPES.direct]
        }

        return nextOverrides
      })
      await dispatch(updateCampaigns(contentSubsidiaryId))
      addToast(`${scopeLabel} channel updated`, { appearance: 'success' })
      setPendingMediumConfirm(null)
    } catch {
      revertPendingMediumOverride(pendingMediumConfirm)
      addToast(`Failed to update ${scopeLabel.toLowerCase()} channel`, { appearance: 'error' })
    } finally {
      setIsUpdatingMedium(false)
    }
  }

  function renderDeliveryRow(scope, groupCell) {
    const medium = getBulkMediumDropdownValue(campaigns, scope, mediumOverrides)
    const mediumPlaceholder = getBulkMediumDropdownPlaceholder(campaigns, scope, mediumOverrides)

    return (
      <div key={scope} className={styles.deliveryScopeRow}>
        <div className={styles.scopeLabelCell}>{groupCell}</div>
        <div className={styles.channelCell}>
          <Dropdown
            className={styles.mediumDropdown}
            options={inviteMediumDropdownOptions}
            value={medium}
            defaultVal={mediumPlaceholder}
            onChange={event =>
              guardEmailSubscriptionAction(() => openMediumConfirmDialog(scope, event.target.value))
            }
            disabled={isUpdatingMedium}
            isSortedAlphabetically={false}
          />
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={styles.settingsCard}>
        <div className={styles.scopeToolbar}>
          <span className={styles.scopeToolbarLabel}>Apply to</span>
          <Dropdown
            className={styles.scopeDropdown}
            options={CAMPAIGN_GROUP_MODE_OPTIONS}
            value={campaignGroupMode}
            onChange={event => handleCampaignGroupModeChange(event.target.value)}
            disabled={isUpdatingMedium}
            isSortedAlphabetically={false}
          />
        </div>

        <div className={styles.settingsTable}>
          <div className={styles.deliveryTableHeader}>
            <span>Campaign group</span>
            <span>Delivery channel</span>
          </div>

          {isCustomMode
            ? CUSTOM_SCOPE_ROWS.map(row =>
                renderDeliveryRow(row.scope, <span className={styles.scopeTitle}>{row.title}</span>)
              )
            : renderDeliveryRow(
                BULK_MEDIUM_SCOPES.all,
                <span className={styles.scopeTitle}>All campaigns</span>
              )}

          {!isCustomMode && scopeStatus.hasSubsetOverrides && (
            <p className={styles.hint}>
              OTA and Direct use different delivery channels. Switch to Custom to edit them
              separately.
            </p>
          )}
        </div>
      </div>

      <div className={styles.mediumLegend}>
        <p className={styles.mediumLegendTitle}>Delivery channel options</p>
        <div className={styles.mediumLegendList}>
          {inviteMediumLegendItems.map(item => (
            <div key={item.medium} className={styles.mediumLegendItem}>
              <p className={styles.mediumLegendLabel}>{item.label}</p>
              <p className={styles.mediumLegendDescription}>{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {pendingMediumConfirm && (
        <ConfirmationDialog
          title="Change delivery channel?"
          positiveMessage="Apply"
          negativeMessage="Cancel"
          onConfirm={applyMediumUpdate}
          onReject={() => {
            revertPendingMediumOverride(pendingMediumConfirm)
            setPendingMediumConfirm(null)
          }}
          isButtonDisabled={isUpdatingMedium}
        >
          <div className={styles.confirmBody}>
            <p className={styles.confirmIntro}>{pendingMediumConfirm.notice}</p>
            <p className={styles.confirmIntro}>
              The following {pendingMediumConfirm.campaignNames.length} campaign
              {pendingMediumConfirm.campaignNames.length === 1 ? '' : 's'} will be updated:
            </p>
            <ul className={styles.campaignList}>
              {pendingMediumConfirm.campaignNames.map((name, index) => (
                <li key={`${name}-${index}`} className={styles.campaignListItem}>
                  {name}
                </li>
              ))}
            </ul>
          </div>
        </ConfirmationDialog>
      )}
    </>
  )
}

export default InviteDeliveryConfigurationSettings
